import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FaCheckCircle, FaExclamationTriangle, FaMoneyBillWave, FaReceipt, FaWallet } from 'react-icons/fa';
import { toast } from 'react-toastify';
import LoadingScreen from '../../../Reutilizables/FetchWithGif';
import { abrirCaja, cerrarCaja, getCajaEstado, getCajaResumen } from '../../../../js/cajaService';
import CajaTables from './components/CajaTables';
import ConfirmationModal from './components/modals/ConfirmationModal';

const formatMoney = (value) => {
  const numberValue = Number(value);
  if (!Number.isFinite(numberValue)) return 'S/ 0.00';
  return `S/ ${numberValue.toFixed(2)}`;
};

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString('es-PE');
};

const toNumber = (value) => {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : 0;
};

function StatCard({ title, value, icon, border = 'border-rose-600', iconBg = 'bg-rose-100', iconColor = 'text-rose-600' }) {
  return (
    <div className={`rounded-2xl border bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md ${border}`}>
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">{title}</div>
          <div className="text-2xl font-bold text-slate-900">{value}</div>
        </div>
        <div className={`${iconBg} ${iconColor} p-3 rounded-xl`}>{icon}</div>
      </div>
    </div>
  );
}

function SectionCard({ title, subtitle, action, children }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
      {(title || subtitle || action) && (
        <div className="flex flex-wrap items-start justify-between gap-4 border-b border-slate-200 px-5 py-4">
          <div>
            {title && <h2 className="text-xl font-semibold text-slate-900">{title}</h2>}
            {subtitle && <p className="text-sm text-slate-500">{subtitle}</p>}
          </div>
          {action}
        </div>
      )}
      <div className="p-5">{children}</div>
    </div>
  );
}

function Pill({ children, tone = 'neutral' }) {
  const tones = {
    neutral: 'bg-slate-100 text-slate-700',
    success: 'bg-emerald-100 text-emerald-800',
    warning: 'bg-amber-100 text-amber-800',
    danger: 'bg-red-100 text-red-800',
  };

  return (
    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${tones[tone] || tones.neutral}`}>
      {children}
    </span>
  );
}

export default function CajaModule() {
  const [loading, setLoading] = useState(false);
  const [caja, setCaja] = useState(null);
  const [totales, setTotales] = useState(null);
  const [detalle, setDetalle] = useState([]);
  const [agrupado, setAgrupado] = useState([]);

  const [montoInicial, setMontoInicial] = useState('');
  const [observacionApertura, setObservacionApertura] = useState('');

  const [montoCierreDeclarado, setMontoCierreDeclarado] = useState('');
  const [observacionCierre, setObservacionCierre] = useState('');

  const [confirmState, setConfirmState] = useState({ show: false, action: null, amount: 0 });

  const cajaAbierta = useMemo(() => caja && caja.estado === 'abierta', [caja]);

  const fetchAndSetResumen = useCallback(async (idCaja) => {
    const data = await getCajaResumen({ idCaja, agrupado_por_operacion: true });
    const payload = data.data || {};
    setTotales(payload.totales || null);
    setDetalle(Array.isArray(payload.detalle) ? payload.detalle : []);
    setAgrupado(Array.isArray(payload.agrupado_por_operacion) ? payload.agrupado_por_operacion : []);
  }, []);

  const refreshAll = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getCajaEstado();
      const cajaAbiertaApi = data.data?.caja_abierta ?? null;
      const ultimaCaja = data.data?.ultima_caja ?? null;
      const cajaTarget = cajaAbiertaApi ?? ultimaCaja;

      setCaja(cajaTarget);

      if (cajaTarget?.idCaja) {
        await fetchAndSetResumen(cajaTarget.idCaja);
      } else {
        setTotales(null);
        setDetalle([]);
        setAgrupado([]);
      }
    } catch (error) {
      console.error(error);
      toast.error(error.message || 'Error al cargar la caja');
    } finally {
      setLoading(false);
    }
  }, [fetchAndSetResumen]);

  useEffect(() => {
    refreshAll();
  }, [refreshAll]);

  const doAbrirCaja = async () => {
    setLoading(true);
    try {
      const payload = {
        monto_inicial: montoInicial !== '' ? toNumber(montoInicial) : undefined,
        observacion: observacionApertura.trim() || undefined,
      };

      const data = await abrirCaja(payload);
      toast.success('Caja abierta');
      setMontoInicial('');
      setObservacionApertura('');

      const cajaNueva = data.data?.caja ?? null;
      setCaja(cajaNueva);
      if (cajaNueva?.idCaja) {
        await fetchAndSetResumen(cajaNueva.idCaja);
      }
    } catch (error) {
      console.error(error);
      toast.error(error.message || 'Error al abrir caja');
    } finally {
      setLoading(false);
    }
  };

  const requestAbrirCaja = () => {
    const monto = montoInicial !== '' ? toNumber(montoInicial) : 0;
    setConfirmState({ show: true, action: 'abrir', amount: monto });
  };

  const doCerrarCaja = async () => {
    if (montoCierreDeclarado === '') {
      toast.error('Ingrese el monto de cierre declarado');
      return;
    }

    setLoading(true);
    try {
      const data = await cerrarCaja({
        idCaja: caja?.idCaja,
        monto_cierre_declarado: toNumber(montoCierreDeclarado),
        observacion: observacionCierre.trim() || undefined,
      });

      toast.success('Caja cerrada');
      setMontoCierreDeclarado('');
      setObservacionCierre('');

      const cajaCerrada = data.data?.caja ?? null;
      if (cajaCerrada?.idCaja) {
        setCaja(cajaCerrada);
        await fetchAndSetResumen(cajaCerrada.idCaja);
      } else {
        await refreshAll();
      }
    } catch (error) {
      console.error(error);
      toast.error(error.message || 'Error al cerrar caja');
    } finally {
      setLoading(false);
    }
  };

  const requestCerrarCaja = () => {
    if (montoCierreDeclarado === '') {
      toast.error('Ingrese el monto de cierre declarado');
      return;
    }

    const monto = toNumber(montoCierreDeclarado);
    setConfirmState({ show: true, action: 'cerrar', amount: monto });
  };

  const statusPill = useMemo(() => {
    if (!caja) return <Pill tone="neutral">SIN CAJA</Pill>;
    if (cajaAbierta) return <Pill tone="success">CAJA ABIERTA</Pill>;
    return <Pill tone="warning">CAJA CERRADA</Pill>;
  }, [caja, cajaAbierta]);

  const diferencia = toNumber(totales?.diferencia);
  const inputClass =
    'w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 transition focus:border-red-400 focus:outline-none focus:ring-2 focus:ring-red-200';

  return (
    <div className="space-y-6">
      {loading && <LoadingScreen />}

      <section className="ui-card relative overflow-hidden px-6 py-7 md:px-8">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_88%_10%,rgba(217,44,52,0.12),transparent_40%)]" />
        <div className="relative">
          <p className="text-xs font-semibold uppercase tracking-[0.28em] text-red-600">Modulo cajero</p>
          <h1 className="mt-2 text-2xl font-semibold text-slate-900 md:text-3xl">Control de caja diaria</h1>
          <p className="mt-2 text-sm text-slate-600">
            Abre y cierra caja, revisa diferencias y audita pagos registrados por operacion.
          </p>
        </div>
      </section>

      <SectionCard
        title="Caja"
        subtitle="Apertura, cierre y resumen de ingresos"
        action={
          <div className="flex items-center gap-2">
            {statusPill}
          </div>
        }
      >
        {cajaAbierta ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-sm text-slate-600">Apertura</div>
                  <div className="font-semibold text-slate-900">{formatDateTime(caja?.fecha_apertura)}</div>
                </div>
                <Pill tone="success">#{caja?.idCaja}</Pill>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-3">
                <div>
                  <div className="text-sm text-slate-600">Monto inicial</div>
                  <div className="font-semibold text-slate-900">{formatMoney(caja?.monto_inicial)}</div>
                </div>
                <div>
                  <div className="text-sm text-slate-600">Pagos registrados</div>
                  <div className="font-semibold text-slate-900">{String(totales?.cantidad_pagos ?? 0)}</div>
                </div>
              </div>
            </div>

            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <div className="text-sm text-slate-600">Diferencia</div>
              <div className="mt-1 flex items-center gap-2">
                {diferencia === 0 ? (
                  <FaCheckCircle className="text-emerald-600" />
                ) : (
                  <FaExclamationTriangle className="text-amber-600" />
                )}
                <div className="text-xl font-bold text-slate-900">{formatMoney(totales?.diferencia)}</div>
              </div>
              <div className="mt-1 text-xs text-slate-500">
                Se calcula al cierre comparando el monto declarado vs el total del sistema.
              </div>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2 rounded-xl border border-slate-200 bg-slate-50 p-4">
              <h3 className="text-lg font-semibold text-slate-900">Abrir caja</h3>
              <p className="text-sm text-slate-500">Registra el monto inicial antes de empezar a cobrar.</p>

              <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700">Monto inicial (S/)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={montoInicial}
                    onChange={(e) => setMontoInicial(e.target.value)}
                    className={inputClass}
                    placeholder="0.00"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="mb-1 block text-sm font-medium text-slate-700">Observacion (opcional)</label>
                  <input
                    type="text"
                    value={observacionApertura}
                    onChange={(e) => setObservacionApertura(e.target.value)}
                    className={inputClass}
                    placeholder="Detalle de apertura de caja"
                  />
                </div>
              </div>

              <div className="mt-4 flex justify-end">
                <button
                  onClick={requestAbrirCaja}
                  className="rounded-xl bg-red-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-red-700 disabled:cursor-not-allowed disabled:bg-gray-400"
                  disabled={loading}
                >
                  Abrir caja
                </button>
              </div>
            </div>

            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <h4 className="text-sm font-semibold text-slate-900">Recomendacion</h4>
              <p className="mt-2 text-sm text-slate-600">
                Antes de registrar pagos, asegurate de abrir caja. Sin caja abierta, el sistema no permitira pagos
                de cajero.
              </p>
            </div>
          </div>
        )}
      </SectionCard>

      {caja && (
        <>
          <div className="flex justify-center">
            <section className="mt-1 w-full max-w-6xl grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <StatCard
                title="Ingresos"
                value={formatMoney(totales?.total_ingresos)}
                border="border-emerald-200"
                iconBg="bg-emerald-100"
                iconColor="text-emerald-600"
                icon={<FaMoneyBillWave className="w-6 h-6" />}
              />
              <StatCard
                title="Total sistema"
                value={formatMoney(totales?.total_sistema)}
                border="border-rose-200"
                iconBg="bg-rose-100"
                iconColor="text-rose-600"
                icon={<FaWallet className="w-6 h-6" />}
              />
              <StatCard
                title="Pagos"
                value={String(totales?.cantidad_pagos ?? 0)}
                border="border-indigo-200"
                iconBg="bg-indigo-100"
                iconColor="text-indigo-600"
                icon={<FaReceipt className="w-6 h-6" />}
              />
              <StatCard
                title="Diferencia"
                value={formatMoney(totales?.diferencia)}
                border={diferencia === 0 ? 'border-emerald-200' : 'border-amber-200'}
                iconBg={diferencia === 0 ? 'bg-emerald-100' : 'bg-amber-100'}
                iconColor={diferencia === 0 ? 'text-emerald-600' : 'text-amber-600'}
                icon={diferencia === 0 ? <FaCheckCircle className="w-6 h-6" /> : <FaExclamationTriangle className="w-6 h-6" />}
              />
            </section>
          </div>

          {cajaAbierta ? (
            <SectionCard title="Cerrar caja" subtitle="Registra el efectivo contado para comparar contra el sistema">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700">Monto de cierre declarado (S/)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={montoCierreDeclarado}
                    onChange={(e) => setMontoCierreDeclarado(e.target.value)}
                    className={inputClass}
                    placeholder="0.00"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="mb-1 block text-sm font-medium text-slate-700">Observacion (opcional)</label>
                  <input
                    type="text"
                    value={observacionCierre}
                    onChange={(e) => setObservacionCierre(e.target.value)}
                    className={inputClass}
                    placeholder="Detalle de cierre"
                  />
                </div>
              </div>

              <div className="mt-4 flex justify-end">
                <button
                  onClick={requestCerrarCaja}
                  className="rounded-xl bg-red-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-red-700 disabled:cursor-not-allowed disabled:bg-gray-400"
                  disabled={loading}
                >
                  Cerrar caja
                </button>
              </div>
            </SectionCard>
          ) : null}

          <CajaTables
            detalle={detalle}
            agrupado={agrupado}
            formatMoney={formatMoney}
            formatDateTime={formatDateTime}
            toNumber={toNumber}
          />
        </>
      )}

      <ConfirmationModal
        show={confirmState.show}
        title={confirmState.action === 'abrir' ? 'Confirmar apertura de caja' : 'Confirmar cierre de caja'}
        message={
          confirmState.action === 'abrir'
            ? `Estas seguro de abrir caja con el monto inicial de ${formatMoney(confirmState.amount)}?`
            : `Estas seguro de cerrar caja con el monto declarado de ${formatMoney(confirmState.amount)}?`
        }
        onClose={() => setConfirmState({ show: false, action: null, amount: 0 })}
        onConfirm={async () => {
          const action = confirmState.action;
          setConfirmState({ show: false, action: null, amount: 0 });
          if (action === 'abrir') {
            await doAbrirCaja();
          } else if (action === 'cerrar') {
            await doCerrarCaja();
          }
        }}
        confirmDisabled={loading}
      />
    </div>
  );
}
